import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  BarChart3, TrendingUp, Users, Calendar, Clock,
  ChevronUp, ChevronDown, Minus, MapPin, Briefcase
} from "lucide-react";

// Simple donut chart component
function DonutChart({ data, size = 120, strokeWidth = 20 }) {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  if (total === 0) return null;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {data.map((item, i) => {
        const pct = item.value / total;
        const dashLength = pct * circumference;
        const dashOffset = -offset;
        offset += dashLength;
        return (
          <circle
            key={i}
            cx={size / 2} cy={size / 2} r={radius}
            fill="none" stroke={item.color} strokeWidth={strokeWidth}
            strokeDasharray={`${dashLength} ${circumference - dashLength}`}
            strokeDashoffset={dashOffset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        );
      })}
      <text x={size / 2} y={size / 2 - 6} textAnchor="middle" className="text-2xl font-bold fill-gray-800">{total}</text>
      <text x={size / 2} y={size / 2 + 12} textAnchor="middle" className="text-xs fill-gray-500">total</text>
    </svg>
  );
}

// Horizontal bar component
function HBar({ label, value, max, color = "bg-purple-500" }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-gray-600 w-28 truncate" title={label}>{label}</span>
      <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden relative">
        <div className={`h-full ${color} rounded-full transition-all duration-700`} style={{ width: `${Math.max(pct, 2)}%` }} />
        <span className="absolute inset-y-0 right-2 flex items-center text-xs font-medium text-gray-600">{value}</span>
      </div>
    </div>
  );
}

const COLORS = ["#8B5CF6", "#EC4899", "#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#6366F1", "#14B8A6"];

export default function AnalyticsTab({ subCategoryData, categoryColors, category }) {
  const [timeRange, setTimeRange] = useState("30d");

  const members = subCategoryData?.members || [];
  const pendingInvites = subCategoryData?.pending_invites || [];

  // Core stats
  const totalInvited = members.length + pendingInvites.length;
  const accepted = members.length;
  const ndaSigned = members.filter(m => m.nda_signed).length;
  const intakeComplete = members.filter(m => m.intake_completed).length;
  const completedIntakes = members.filter(m => m.intake_completed && m.intake_responses);

  const conversionRates = useMemo(() => ({
    inviteToAccept: totalInvited > 0 ? Math.round((accepted / totalInvited) * 100) : 0,
    acceptToNda: accepted > 0 ? Math.round((ndaSigned / accepted) * 100) : 0,
    ndaToIntake: ndaSigned > 0 ? Math.round((intakeComplete / ndaSigned) * 100) : 0,
    overallConversion: totalInvited > 0 ? Math.round((intakeComplete / totalInvited) * 100) : 0
  }), [totalInvited, accepted, ndaSigned, intakeComplete]);

  // INTAKE DATA DISTRIBUTIONS
  const intakeData = useMemo(() => {
    const cities = {};
    const services = {};
    const experience = {};
    const genders = {};
    const platforms = {};

    completedIntakes.forEach(m => {
      const r = m.intake_responses || {};

      // City / Location
      const city = r.city || r.location || r.user_city || "";
      if (city) cities[city] = (cities[city] || 0) + 1;

      // Services offered
      const svc = r.services_offered || r.services || "";
      if (typeof svc === "string" && svc) {
        svc.split(",").map(s => s.trim()).filter(Boolean).forEach(s => {
          services[s] = (services[s] || 0) + 1;
        });
      } else if (Array.isArray(svc)) {
        svc.forEach(s => { services[s] = (services[s] || 0) + 1; });
      }

      // Years experience
      const yrs = r.years_experience || "";
      if (yrs) experience[yrs] = (experience[yrs] || 0) + 1;

      // Gender
      const gender = r.gender || "";
      if (gender) genders[gender] = (genders[gender] || 0) + 1;

      // Booking platforms
      const bp = r.booking_platforms || [];
      if (Array.isArray(bp)) {
        bp.forEach(p => { platforms[p] = (platforms[p] || 0) + 1; });
      } else if (typeof bp === "string" && bp) {
        bp.split(",").map(p => p.trim()).filter(Boolean).forEach(p => {
          platforms[p] = (platforms[p] || 0) + 1;
        });
      }
    });

    const toSorted = (obj) => Object.entries(obj).sort((a, b) => b[1] - a[1]).map(([label, value]) => ({ label, value }));

    return {
      cities: toSorted(cities),
      services: toSorted(services),
      experience: toSorted(experience),
      genders: toSorted(genders),
      platforms: toSorted(platforms),
    };
  }, [completedIntakes]);

  // Status distribution for donut
  const statusData = useMemo(() => {
    const invited = pendingInvites.length;
    const active = members.filter(m => m.intake_completed).length;
    const inProgress = members.filter(m => !m.intake_completed && m.nda_signed).length;
    const pending = members.filter(m => !m.nda_signed).length;
    return [
      { label: "Active", value: active, color: "#10B981" },
      { label: "In Progress", value: inProgress, color: "#3B82F6" },
      { label: "Pending", value: pending, color: "#F59E0B" },
      { label: "Invited", value: invited, color: "#D1D5DB" },
    ].filter(d => d.value > 0);
  }, [members, pendingInvites]);

  // Join timeline
  const joinsByDate = useMemo(() => {
    const grouped = {};
    const now = new Date();
    const daysBack = timeRange === "7d" ? 7 : timeRange === "30d" ? 30 : 90;
    for (let i = 0; i < daysBack; i++) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      const key = date.toISOString().split("T")[0];
      grouped[key] = { date: key, count: 0 };
    }
    members.forEach(m => {
      if (m.created_at) {
        const key = new Date(m.created_at).toISOString().split("T")[0];
        if (grouped[key]) grouped[key].count++;
      }
    });
    return Object.values(grouped).reverse();
  }, [members, timeRange]);

  const maxCount = Math.max(...joinsByDate.map(d => d.count), 1);

  return (
    <div className="p-6 space-y-6" data-testid="analytics-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          Analytics Overview
        </h3>
        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
            <SelectItem value="90d">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Conversion Funnel */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: "Invite → Accept", rate: conversionRates.inviteToAccept, detail: `${accepted} of ${totalInvited}` },
          { label: "Accept → NDA", rate: conversionRates.acceptToNda, detail: `${ndaSigned} of ${accepted}` },
          { label: "NDA → Intake", rate: conversionRates.ndaToIntake, detail: `${intakeComplete} of ${ndaSigned}` },
          { label: "Overall", rate: conversionRates.overallConversion, detail: "End-to-end", highlight: true }
        ].map((step, i) => (
          <Card key={i} className={step.highlight ? `${categoryColors.light} ${categoryColors.border} border` : ""}>
            <CardContent className="pt-4 pb-4">
              <span className={`text-xs uppercase ${step.highlight ? categoryColors.text : "text-gray-500"}`}>{step.label}</span>
              <p className={`text-3xl font-bold mt-1 ${step.highlight ? categoryColors.text : ""}`}>{step.rate}%</p>
              <p className="text-xs text-gray-500 mt-1">{step.detail}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Status & Gender Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Distribution */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Users className="w-4 h-4" />
              Status Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              <DonutChart data={statusData} />
              <div className="space-y-2 flex-1">
                {statusData.map((d, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: d.color }} />
                      <span className="text-sm">{d.label}</span>
                    </div>
                    <span className="text-sm font-medium">{d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Gender Breakdown */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Gender Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            {intakeData.genders.length > 0 ? (
              <div className="flex items-center gap-6">
                <DonutChart
                  data={intakeData.genders.map((g, i) => ({ ...g, color: COLORS[i % COLORS.length] }))}
                />
                <div className="space-y-2 flex-1">
                  {intakeData.genders.map((g, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                        <span className="text-sm capitalize">{g.label}</span>
                      </div>
                      <span className="text-sm font-medium">{g.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-400 py-4 text-center">No gender data yet. Will appear as intake responses come in.</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Intake Response Data */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* City Distribution */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Location / City
            </CardTitle>
          </CardHeader>
          <CardContent>
            {intakeData.cities.length > 0 ? (
              <div className="space-y-2">
                {intakeData.cities.slice(0, 8).map((c, i) => (
                  <HBar key={i} label={c.label} value={c.value} max={intakeData.cities[0]?.value || 1} color={COLORS[i % COLORS.length].replace("#", "bg-[#") + "]"} />
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 py-4 text-center">No location data yet</p>
            )}
          </CardContent>
        </Card>

        {/* Services Offered */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Briefcase className="w-4 h-4" />
              Services Offered
            </CardTitle>
          </CardHeader>
          <CardContent>
            {intakeData.services.length > 0 ? (
              <div className="space-y-2">
                {intakeData.services.slice(0, 8).map((s, i) => (
                  <HBar key={i} label={s.label} value={s.value} max={intakeData.services[0]?.value || 1} color={COLORS[(i + 2) % COLORS.length].replace("#", "bg-[#") + "]"} />
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 py-4 text-center">No services data yet</p>
            )}
          </CardContent>
        </Card>

        {/* Years of Experience */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Years of Experience
            </CardTitle>
          </CardHeader>
          <CardContent>
            {intakeData.experience.length > 0 ? (
              <div className="space-y-2">
                {intakeData.experience.map((e, i) => (
                  <HBar key={i} label={`${e.label} years`} value={e.value} max={intakeData.experience[0]?.value || 1} color={COLORS[(i + 4) % COLORS.length].replace("#", "bg-[#") + "]"} />
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 py-4 text-center">No experience data yet</p>
            )}
          </CardContent>
        </Card>

        {/* Booking Platforms */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Booking Platforms Used
            </CardTitle>
          </CardHeader>
          <CardContent>
            {intakeData.platforms.length > 0 ? (
              <div className="space-y-2">
                {intakeData.platforms.slice(0, 8).map((p, i) => (
                  <HBar key={i} label={p.label} value={p.value} max={intakeData.platforms[0]?.value || 1} color={COLORS[(i + 1) % COLORS.length].replace("#", "bg-[#") + "]"} />
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 py-4 text-center">No platform data yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Join Timeline */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Join Timeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-32 flex items-end gap-1">
            {joinsByDate.slice(-30).map((day, idx) => (
              <div key={idx} className="flex-1 flex flex-col items-center group relative">
                <div
                  className={`w-full ${categoryColors.bg} rounded-t opacity-70 group-hover:opacity-100 transition-opacity cursor-pointer`}
                  style={{ height: `${(day.count / maxCount) * 100}%`, minHeight: day.count > 0 ? "4px" : "0" }}
                />
                <div className="absolute bottom-full mb-1 bg-gray-900 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-10">
                  {day.date}: {day.count} joins
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-xs text-gray-400">
            <span>{joinsByDate[0]?.date}</span>
            <span>{joinsByDate[joinsByDate.length - 1]?.date}</span>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4 pb-4 text-center">
            <Clock className="w-6 h-6 mx-auto mb-2 text-gray-400" />
            <p className="text-2xl font-bold">
              {ndaSigned > 0 ? `${Math.round(members.reduce((acc, m) => {
                if (m.created_at && m.nda_signed_at) return acc + (new Date(m.nda_signed_at) - new Date(m.created_at)) / (1000 * 60 * 60);
                return acc;
              }, 0) / ndaSigned)}h` : " - "}
            </p>
            <p className="text-xs text-gray-500">Avg. Time to NDA</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4 text-center">
            <TrendingUp className="w-6 h-6 mx-auto mb-2 text-gray-400" />
            <p className="text-2xl font-bold">{intakeComplete}</p>
            <p className="text-xs text-gray-500">Fully Onboarded</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4 text-center">
            <Users className="w-6 h-6 mx-auto mb-2 text-gray-400" />
            <p className="text-2xl font-bold">{pendingInvites.length}</p>
            <p className="text-xs text-gray-500">Awaiting Response</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
